import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  CircularProgress,
  TextField,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';

import Actions from '../../../../store/ducks/appointments';
import RatingChart from '../../../../components/RatingChart';
import useStyles from './styles';

const RatingModal = ({ handleExited, appointmentId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const appointments = useSelector(state => state.appointments.history.list);
  const [open, setOpen] = useState(true);
  const [ratingInfos, setRatingInfos] = useState({});
  const [sendingRating, setSendingRating] = useState(false);
  const [editable, setEditable] = useState(true);
  const [commentError, setCommentError] = useState({});

  useEffect(() => {
    if (!appointmentId) return;
    if (appointmentId in appointments) {
      const { rating, comments } = appointments[appointmentId];
      const error = appointments[appointmentId]?.error;
      setRatingInfos(oldRatingInfos => {
        const errorSend = error?.action === 'storeRating' ? error : false;
        return {
          rating: errorSend ? oldRatingInfos?.rating : rating,
          comments: errorSend ? oldRatingInfos?.comments : comments || '',
        };
      });
      if (rating) {
        setEditable(false);
      }
    }
  }, [appointmentId, appointments]);

  useEffect(() => {
    if (!appointmentId) return;
    const error = appointments[appointmentId]?.error;
    const success = appointments[appointmentId]?.success;

    if (error?.action === 'storeRating') {
      enqueueSnackbar(error.msg, { variant: 'error' });
      setSendingRating(false);
    }
    if (success?.action === 'storeRating') {
      enqueueSnackbar(success.msg, { variant: 'success' });
      setSendingRating(false);
    }
  }, [appointmentId, dispatch, enqueueSnackbar, appointments]);

  useEffect(
    () => () => {
      dispatch(
        Actions.updateAppointment(appointmentId, {
          error: {},
          success: {},
        }),
      );
    },
    [appointmentId, dispatch],
  );

  const sendRating = useCallback(() => {
    const { rating, comments } = ratingInfos;
    if (!!rating && !!comments) {
      dispatch(Actions.requestStoreRating(appointmentId, rating, comments));
      setSendingRating(true);
    } else {
      if (!rating) {
        enqueueSnackbar('Você precisa atribuir uma nota ao agendamento.', {
          variant: 'error',
        });
      }
      if (!comments) {
        setCommentError({
          helperText: 'Campo obrigatório.',
          error: true,
        });
      }
    }
  }, [ratingInfos, dispatch, appointmentId, enqueueSnackbar]);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClickSend = useCallback(() => {
    if (!sendingRating) {
      sendRating();
    }
  }, [sendingRating, sendRating]);

  const handleChangeRating = useCallback(
    rating => {
      if (!sendingRating) {
        setRatingInfos(oldRatingInfos => ({ ...oldRatingInfos, rating }));
      }
    },
    [sendingRating],
  );

  const handleChangeComment = useCallback(
    e => {
      if (!sendingRating) {
        const newValue = e.target?.value;
        setRatingInfos(oldRatingInfos => ({
          ...oldRatingInfos,
          comments: newValue,
        }));
        if (Object.keys(commentError).length && newValue) {
          setCommentError({});
        }
      }
    },
    [sendingRating, commentError],
  );

  return (
    <Dialog
      className={classes.container}
      classes={{ paper: classes.paper }}
      onClose={handleCloseModal}
      onExited={handleExited}
      open={open}
      maxWidth="xs"
    >
      <DialogContent>
        <Box className="rating-content">
          <Typography className="rating-title" variant="h6">
            Como foi o agendamento?
          </Typography>
          <Box className="rating-chart-container">
            <RatingChart
              className="rating-chart"
              rate={ratingInfos.rating}
              size="big"
              onChange={editable && !sendingRating ? handleChangeRating : false}
              showLegend
            />
          </Box>
          <TextField
            className="rating-comment"
            variant="outlined"
            multiline
            rows={4}
            InputLabelProps={{ shrink: true }}
            label="Comentário"
            placeholder="Descreva como foi a sua experiência..."
            value={ratingInfos.comments}
            onChange={e => handleChangeComment(e)}
            inputProps={{ readOnly: !editable || sendingRating }}
            {...commentError}
          />
        </Box>
      </DialogContent>
      <DialogActions className="dialog-footer">
        <Button
          onClick={handleCloseModal}
          color="secondary"
          disabled={sendingRating}
        >
          Fechar
        </Button>
        {editable && (
          <Button onClick={handleClickSend} color="primary">
            {sendingRating ? (
              <CircularProgress color="inherit" size={24} />
            ) : (
              'Enviar'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RatingModal;
